/// <amd-module name="Search/WorkItem/Components/PivotCommands" />

import * as Resources from "Search/Shared/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { IAggregatedState } from "Search/WorkItem/Flux/StoresHub";
import { ActionCreator } from "Search/WorkItem/Flux/ActionCreator";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar"
import { IPivotCommands } from "Search/Shared/Components/PivotCommands";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { EntitySortOption } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { ISortField, ISortOption, IPreviewSetting } from "Search/Shared/Base/Types";
import { SortActionIds } from "Search/WorkItem/Constants";
import { format } from "VSS/Core/Util/String";

export function handlePivotCommands(
    pivotCommands: ObservableValue<IPivotCommands>,
    getAggregatedState: () => IAggregatedState,
    actionCreator: ActionCreator,
    isMember: boolean): void {
    const {
        isProjectContext,
        availableSortFields,
        appliedEntitySortOption,
        previewOrientationStoreState,
        availablePreviewOrientations,
        filterStoreState
    } = getAggregatedState();
    const {
        clickAccountButton,
        toggleFilterPane
    } = actionCreator;

    const { filter, filterItemsVisible } = filterStoreState;
    const toggleButtonTooltip = filterItemsVisible ? Resources.HideFilterPanel : Resources.ShowFilterPanel;
    const filterToggle: IHeaderCommandBarItem = {
        id: "filterToggle",
        ariaLabel: toggleButtonTooltip,
        subtle: true,
        tooltipProps: { text: toggleButtonTooltip },
        iconProps: { iconName: filter.hasChangesToReset() ? "FilterSolid" : "Filter" },
        onActivate: item => toggleFilterPane()
    }

    const settingsPivot = getPreviewOrientation(previewOrientationStoreState.previewOrientation, availablePreviewOrientations, actionCreator.changePreviewOrienation);

    const showSearchThisAccountButton = isProjectContext && isMember;
    let items: IHeaderCommandBarItem[] = showSearchThisAccountButton ? [{
        subtle: true,
        text: Resources.SearchThisAccountButtonText,
        id: "searchAccountLink",
        iconProps: { iconName: "ReplyMirrored" },
        onActivate: item => clickAccountButton()
    }] : [];

    const sortOption = getSortOptions(appliedEntitySortOption, availableSortFields, actionCreator.changeSortCriteria);
    const farItems = [sortOption, settingsPivot, filterToggle];

    pivotCommands.value = { farItems, items };
}


function getSortOptions(
    currentSortOption: EntitySortOption,
    availableOptions: ISortField[],
    changeSortCriteria: (option: ISortOption) => void): IHeaderCommandBarItem {
    const { field, sortOrder } = currentSortOption;
    const divider: IMenuItem = {
        id: "divider",
        itemType: MenuItemType.Divider
    };
    const ascending: IMenuItem = {
        id: "ascending",
        text: Resources.Ascending,
        checked: sortOrder === "asc",
        readonly: true,
        onActivate: item => changeSortCriteria({ order: "asc", key: field }),
        disabled: field === SortActionIds.Relevance
    };
    const descending: IMenuItem = {
        id: "descending",
        text: Resources.Descending,
        checked: sortOrder === "desc",
        readonly: true,
        onActivate: item => changeSortCriteria({ order: "desc", key: field }),
        disabled: field === SortActionIds.Relevance
    };
    const sortText = availableOptions.filter(sf => sf.key == field)[0].name;
    const sortOptionsMenuItems = availableOptions.map<IMenuItem>(sf => {
        return {
            id: sf.key,
            text: sf.name,
            checked: sf.key === field,
            readonly: true,
            onActivate: (item: IMenuItem) => {
                changeSortCriteria({ order: sortOrder, key: item.id });
            }
        }
    });

    const tooltip = format(Resources.SortOptionTooltip, sortText);
    return {
        id: "sortButton",
        subtle: true,
        text: sortText,
        subMenuProps: {
            id: "sortOptions",
            ariaLabel: Resources.SortByHeader,
            items: [
                {
                    id: "option-header",
                    itemType: MenuItemType.Header,
                    text: Resources.SortByHeader
                },
                ...sortOptionsMenuItems,
                divider,
                ascending,
                descending
            ]
        },
        ariaLabel: tooltip,
        tooltipProps: { text: tooltip }
    };
}

function getPreviewOrientation(
    currentOrienation: string,
    availableOptions: IPreviewSetting[],
    changePreviewOrienation: (orientation: IPreviewSetting) => void): IHeaderCommandBarItem {
    const previewOptionMenuItems = availableOptions.map(po => {
        return {
            id: po.key,
            text: po.name,
            checked: po.key === currentOrienation,
            readonly: true,
            onActivate: (item: IMenuItem) => changePreviewOrienation({ key: item.id, name: item.text })
        }
    });

    const currentOrientation = availableOptions.filter(po => po.key === currentOrienation);
    const previewTooltip = `${Resources.PreviewOrientationLabel}: ${currentOrientation[0] && currentOrientation[0].name}`;
    const settingsPivot: IHeaderCommandBarItem = {
        id: "viewSetting",
        subtle: true,
        iconProps: { iconName: "Equalizer" },
        subMenuProps: {
            id: "viewOptions",
            ariaLabel: Resources.PreviewOrientationLabel,
            items: [
                {
                    id: "preview-header",
                    itemType: MenuItemType.Header,
                    text: Resources.PreviewOrientationLabel
                },
                ...previewOptionMenuItems
            ]
        },
        ariaLabel: previewTooltip,
        tooltipProps: { text: previewTooltip }
    }

    return settingsPivot;
}